import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaUser, FaLock, FaEye, FaEyeSlash, FaSignInAlt, FaEnvelope, FaArrowLeft, FaUserPlus } from 'react-icons/fa';
import { API_BASE_URL, APPLICATION_ID, AUTH_URL, CORE_ONBOARDING_ORIGIN } from '../config';
import { LoadingButton } from '../components/LoadingSpinner';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../context/PermissionsContext';
import { supabase, getResetPasswordRedirectUrl } from '../supabaseClient';
import { persistAuthSession, resolvePostAuthPath } from '../utils/authSession';
import {
    buildCoreOnboardingUrl,
    createOnboardingState,
    extractHandoffErrorMessage,
    getOnboardingReturnUrl,
    hasHandoffReturn,
    isMissingApplicationAccess,
} from '../utils/coreOnboarding';
import './login.css';

function Login() {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isForgotPassword, setIsForgotPassword] = useState(false);
    const [resetEmail, setResetEmail] = useState('');
    const [resetStatus, setResetStatus] = useState(null); // 'sent' | 'error'
    const [resetError, setResetError] = useState('');
    const [isResetting, setIsResetting] = useState(false);
    const navigate = useNavigate();
    const { clearSessionExpired } = useAuth();
    const { fetchUserPermissions, clearPermissions, hasFlowAccess, isPermissionsLoading } = usePermissions();

    // Clear any leftover session-expired state when landing on login
    useEffect(() => {
        clearSessionExpired();
    }, [clearSessionExpired]);

    useEffect(() => {
        if (!hasHandoffReturn()) {
            return;
        }
        navigate(`/onboarding/callback${window.location.search}${window.location.hash}`, { replace: true });
    }, [navigate]);

    useEffect(() => {
        if (hasHandoffReturn() || isForgotPassword) {
            return;
        }
        const token = localStorage.getItem('token');
        if (!token || isPermissionsLoading) {
            return;
        }
        navigate(hasFlowAccess ? '/home' : '/onboarding/pending', { replace: true });
    }, [hasFlowAccess, isForgotPassword, isPermissionsLoading, navigate]);
    
    // Refs for input fields
    const usernameRef = useRef(null);
    const passwordRef = useRef(null);

    // Function to read actual values from input fields
    const readInputValues = () => {
        if (usernameRef.current) {
            const actualUsername = usernameRef.current.value;
            if (actualUsername !== username) {
                setUsername(actualUsername);
            }
        }
        if (passwordRef.current) {
            const actualPassword = passwordRef.current.value;
            if (actualPassword !== password) {
                setPassword(actualPassword);
            }
        }
    };

    // Check for auto-filled values when component mounts and after a delay
    useEffect(() => {
        // Initial check
        readInputValues();
        
        // Check again after a short delay to catch browser auto-fill
        const timer = setTimeout(() => {
            readInputValues();
        }, 100);
        
        // Check periodically for the first few seconds
        const interval = setInterval(() => {
            readInputValues();
        }, 500);
        
        // Clear interval after 3 seconds
        const clearIntervalTimer = setTimeout(() => {
            clearInterval(interval);
        }, 3000);
        
        return () => {
            clearTimeout(timer);
            clearTimeout(clearIntervalTimer);
            clearInterval(interval);
        };
    }, []);

    const handleForgotPassword = async (event) => {
        event.preventDefault();
        setResetError('');
        setIsResetting(true);

        try {
            const { error } = await supabase.auth.resetPasswordForEmail(resetEmail, {
                redirectTo: getResetPasswordRedirectUrl(),
            });

            if (error) {
                setResetError(error.message);
            } else {
                setResetStatus('sent');
            }
        } catch (err) {
            setResetError('Something went wrong. Please try again.');
        } finally {
            setIsResetting(false);
        }
    };

    const handleBackToLogin = () => {
        setIsForgotPassword(false);
        setResetEmail('');
        setResetStatus(null);
        setResetError('');
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setError('');
        setIsLoading(true);
        
        try {
            const payload = { email: username, password, applicationId: APPLICATION_ID };
            let response = await fetch(`${AUTH_URL}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });

            let data = await response.json().catch(() => ({}));

            if (!response.ok) {
                const detail = extractHandoffErrorMessage(data, '');
                if (response.status === 403 && isMissingApplicationAccess(detail)) {
                    response = await fetch(`${AUTH_URL}`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ email: username, password }),
                    });
                    data = await response.json().catch(() => ({}));
                }
            }

            if (!response.ok) {
                const detail = extractHandoffErrorMessage(data, '');
                if (isMissingApplicationAccess(detail)) {
                    throw new Error('Your Flow access is still being reviewed. Create an account if you have not already, or wait for an administrator to approve you.');
                }
                throw new Error(detail || data.error || 'Login failed');
            }

            persistAuthSession(data);
            clearPermissions();
            clearSessionExpired();

            const tenants = await fetchUserPermissions();
            const nextPath = resolvePostAuthPath(tenants);
            if (nextPath !== '/home') {
                navigate(nextPath);
                return;
            }

            try {
                const accessResponse = await fetch(`${API_BASE_URL}/me/membership-access`, {
                    headers: { Authorization: `Bearer ${data.accessToken}` },
                });
                if (accessResponse.ok) {
                    const access = await accessResponse.json();
                    if (access.awaitingApproval) {
                        navigate('/awaiting-approval');
                        return;
                    }
                }
            } catch (accessErr) {
                console.warn('Membership access check failed:', accessErr);
            }

            navigate('/home');
        } catch (err) {
            setError(err.message);
        } finally {
            setIsLoading(false);
        }
    };

    const handleCreateAccount = () => {
        const state = createOnboardingState();
        window.location.assign(buildCoreOnboardingUrl({
            origin: CORE_ONBOARDING_ORIGIN,
            applicationId: APPLICATION_ID,
            returnUrl: getOnboardingReturnUrl(),
            state,
        }));
    };

    return (
        <div className="login-container">
            <div className="login-background">
                <div className="login-overlay"></div>
            </div>

            <div className="login-content">
                <div className="login-card">
                    <div className="login-header">
                        <div className="logo-container">
                            <img
                                src="/favicon.ico"
                                alt="Wandawazi Flow Logo"
                                className="login-logo"
                                style={{ width: '120px', height: 'auto', maxHeight: '50px', objectFit: 'contain', filter: 'none' }}
                            />
                        </div>
                        <h1 className="login-title">
                            {isForgotPassword ? 'Reset Password' : 'Welcome Back'}
                        </h1>
                        <p className="login-subtitle">
                            {isForgotPassword ? "We'll send a reset link to your email" : 'Sign in to access your dashboard'}
                        </p>
                    </div>

                    {!isForgotPassword ? (
                        <>
                            {error && <div className="error-message">{error}</div>}

                            <form onSubmit={handleSubmit} className="login-form">
                                <div className="form-group">
                                    <label htmlFor="username" className="form-label">
                                        <FaUser className="input-icon" />
                                        Username
                                    </label>
                                    <input
                                        ref={usernameRef}
                                        type="text"
                                        id="username"
                                        className="form-input"
                                        placeholder="Enter your username"
                                        value={username}
                                        onChange={(e) => setUsername(e.target.value)}
                                        onInput={(e) => setUsername(e.target.value)}
                                        onBlur={(e) => setUsername(e.target.value)}
                                        required
                                    />
                                </div>

                                <div className="form-group">
                                    <label htmlFor="password" className="form-label">
                                        <FaLock className="input-icon" />
                                        Password
                                    </label>
                                    <div className="password-input-container">
                                        <input
                                            ref={passwordRef}
                                            type={showPassword ? "text" : "password"}
                                            id="password"
                                            className="form-input"
                                            placeholder="Enter your password"
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            onInput={(e) => setPassword(e.target.value)}
                                            onBlur={(e) => setPassword(e.target.value)}
                                            required
                                        />
                                        <button
                                            type="button"
                                            className="password-toggle"
                                            onClick={() => setShowPassword(!showPassword)}
                                        >
                                            {showPassword ? <FaEyeSlash /> : <FaEye />}
                                        </button>
                                    </div>
                                </div>

                                <LoadingButton
                                    type="submit"
                                    className="login-btn"
                                    isLoading={isLoading}
                                    loadingText="Signing in..."
                                    variant="primary"
                                >
                                    <FaSignInAlt />
                                    Sign In
                                </LoadingButton>

                                <button
                                    type="button"
                                    className="forgot-password-link"
                                    onClick={() => setIsForgotPassword(true)}
                                >
                                    Forgot your password?
                                </button>
                            </form>

                            <div className="create-account-panel">
                                <div className="create-account-divider">
                                    <span>New to Flow?</span>
                                </div>
                                <button
                                    type="button"
                                    className="create-account-btn"
                                    onClick={handleCreateAccount}
                                >
                                    <FaUserPlus />
                                    Create account
                                </button>
                            </div>
                        </>
                    ) : (
                        <form onSubmit={handleForgotPassword} className="login-form">
                            {resetStatus === 'sent' ? (
                                <div className="reset-success-message">
                                    Check your inbox — a reset link has been sent to <strong>{resetEmail}</strong>.
                                </div>
                            ) : (
                                <>
                                    {resetError && <div className="error-message" style={{ margin: '0 0 1rem' }}>{resetError}</div>}

                                    <div className="form-group">
                                        <label htmlFor="resetEmail" className="form-label">
                                            <FaEnvelope className="input-icon" />
                                            Email Address
                                        </label>
                                        <input
                                            type="email"
                                            id="resetEmail"
                                            className="form-input"
                                            placeholder="Enter your email"
                                            value={resetEmail}
                                            onChange={(e) => setResetEmail(e.target.value)}
                                            required
                                            autoFocus
                                        />
                                    </div>

                                    <LoadingButton
                                        type="submit"
                                        className="login-btn"
                                        isLoading={isResetting}
                                        loadingText="Sending..."
                                        variant="primary"
                                    >
                                        Send Reset Link
                                    </LoadingButton>
                                </>
                            )}

                            <button
                                type="button"
                                className="forgot-password-link"
                                onClick={handleBackToLogin}
                            >
                                <FaArrowLeft style={{ fontSize: '0.75rem' }} /> Back to sign in
                            </button>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
}

export default Login;